import { useState } from 'react';
import PanelLayout from '../components/PanelLayout';

type Channel = 'inApp' | 'whatsapp' | 'sms';

interface NotificationSetting {
  id: string;
  label: string;
  critical: boolean;
  inApp: boolean;
  whatsapp: boolean;
  sms: boolean;
}

const criticalNotifications: NotificationSetting[] = [
  { id: 'account-suspended', label: 'Hesabınız askıya alındı / ban', critical: true, inApp: true, whatsapp: true, sms: true },
  { id: 'new-request', label: 'Yeni iletişim talebi geldi', critical: true, inApp: true, whatsapp: true, sms: false },
  { id: 'hiring-approval', label: 'İşe başlama onayı bekleniyor', critical: true, inApp: true, whatsapp: true, sms: false },
  { id: 'conflict-review', label: 'Çelişkili durum admin inceliyor', critical: true, inApp: true, whatsapp: true, sms: true },
  { id: 'complaint-result', label: 'Şikayet sonucu', critical: true, inApp: true, whatsapp: true, sms: true },
];

const normalNotifications: NotificationSetting[] = [
  { id: 'new-match', label: 'Yeni uygun işletme önerisi', critical: false, inApp: true, whatsapp: true, sms: false },
  { id: 'profile-reminder', label: 'Profil tamamlama hatırlatması', critical: false, inApp: true, whatsapp: true, sms: false },
  { id: 'conversation-update', label: 'Görüşme durumu değişikliği', critical: false, inApp: true, whatsapp: true, sms: false },
  { id: 'fav-status', label: 'Favori işletme durumu güncellemesi', critical: false, inApp: true, whatsapp: false, sms: false },
  { id: 'system-maintenance', label: 'Sistem bakımı ve duyurular', critical: false, inApp: true, whatsapp: false, sms: false },
];

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={() => !disabled && onChange()}
      className={`w-11 h-6 rounded-full transition-colors relative ${
        disabled ? 'bg-gray-100 cursor-not-allowed' : checked ? 'bg-dark-teal-500' : 'bg-gray-200'
      }`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-5.5' : 'translate-x-0.5'
        }`}
        style={{ left: checked ? '22px' : '2px' }}
      />
    </button>
  );
}

export default function BildirimPage() {
  const [criticalList, setCriticalList] = useState(criticalNotifications);
  const [normalList, setNormalList] = useState(normalNotifications);
  const [quietStart, setQuietStart] = useState('23:00');
  const [quietEnd, setQuietEnd] = useState('09:00');
  const [quietEnabled, setQuietEnabled] = useState(true);

  const toggleChannel = (
    list: NotificationSetting[],
    setList: React.Dispatch<React.SetStateAction<NotificationSetting[]>>,
    id: string,
    channel: keyof Omit<NotificationSetting, 'id' | 'label' | 'critical'>
  ) => {
    setList((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [channel]: !item[channel] } : item
      )
    );
  };

  return (
    <PanelLayout>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl md:text-2xl font-bold text-deep-navy-500 mb-2">Bildirim Ayarları</h1>
        <p className="text-sm text-gray-500 mb-8">
          Hangi bildirimleri hangi kanaldan alacağınızı yönetin. Kritik bildirimler sessiz saate takılmaz.
        </p>

        {/* Channels Info */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
          <h2 className="text-sm font-semibold text-deep-navy-500 mb-4">Bildirim Kanalları</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-dark-teal-50 text-dark-teal-500 shrink-0">
                <i className="ri-notification-3-line text-sm" />
              </span>
              <div>
                <p className="text-sm font-medium text-deep-navy-500">Sistem İçi</p>
                <p className="text-xs text-gray-500 mt-0.5">Varsayılan, her zaman aktif</p>
                <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-green-50 text-green-600 text-[10px] font-medium">
                  Zorunlu
                </span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-50 text-green-600 shrink-0">
                <i className="ri-whatsapp-line text-sm" />
              </span>
              <div>
                <p className="text-sm font-medium text-deep-navy-500">WhatsApp</p>
                <p className="text-xs text-gray-500 mt-0.5">Opsiyonel, terapist tercihine bağlı</p>
                <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-medium">
                  Opsiyonel
                </span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600 shrink-0">
                <i className="ri-message-3-line text-sm" />
              </span>
              <div>
                <p className="text-sm font-medium text-deep-navy-500">SMS</p>
                <p className="text-xs text-gray-500 mt-0.5">Sadece kritik durumlarda gönderilir</p>
                <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-medium">
                  Kritik Only
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quiet Hours */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-gray-500">
                <i className="ri-moon-line text-sm" />
              </span>
              <h2 className="text-sm font-semibold text-deep-navy-500">Sessiz Saat</h2>
            </div>
            <Toggle checked={quietEnabled} onChange={() => setQuietEnabled(!quietEnabled)} />
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Sessiz saat içinde normal bildirimler gönderilmez. Kritik bildirimler (hesap askıya alma, yeni talep vb.) sessiz saate takılmaz.
          </p>
          {quietEnabled && (
            <div className="flex items-center gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Başlangıç</label>
                <input
                  type="time"
                  value={quietStart}
                  onChange={(e) => setQuietStart(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-deep-navy-500 focus:outline-none focus:border-dark-teal-500"
                />
              </div>
              <span className="text-gray-400 mt-5">—</span>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Bitiş</label>
                <input
                  type="time"
                  value={quietEnd}
                  onChange={(e) => setQuietEnd(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-deep-navy-500 focus:outline-none focus:border-dark-teal-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Critical Notifications */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-100 bg-red-50/50">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-red-500">
                <i className="ri-alarm-warning-line text-sm" />
              </span>
              <h2 className="text-sm font-semibold text-deep-navy-500">Kritik Bildirimler</h2>
              <span className="ml-auto px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px] font-medium">
                Sessiz saate takılmaz
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Bu bildirimler her zaman sistem içi gönderilir. SMS sadece kritik durumlarda.</p>
          </div>
          <div className="divide-y divide-gray-50">
            {criticalList.map((item) => (
              <div key={item.id} className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-deep-navy-500">{item.label}</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Sistem</span>
                    <Toggle checked={item.inApp} onChange={() => toggleChannel(criticalList, setCriticalList, item.id, 'inApp')} disabled />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">WhatsApp</span>
                    <Toggle checked={item.whatsapp} onChange={() => toggleChannel(criticalList, setCriticalList, item.id, 'whatsapp')} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">SMS</span>
                    <Toggle checked={item.sms} onChange={() => toggleChannel(criticalList, setCriticalList, item.id, 'sms')} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Normal Notifications */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-gray-500">
                <i className="ri-notification-3-line text-sm" />
              </span>
              <h2 className="text-sm font-semibold text-deep-navy-500">Normal Bildirimler</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1">Sessiz saat içinde bu bildirimler gönderilmez.</p>
          </div>
          <div className="divide-y divide-gray-50">
            {normalList.map((item) => (
              <div key={item.id} className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-deep-navy-500">{item.label}</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Sistem</span>
                    <Toggle checked={item.inApp} onChange={() => toggleChannel(normalList, setNormalList, item.id, 'inApp')} disabled />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">WhatsApp</span>
                    <Toggle checked={item.whatsapp} onChange={() => toggleChannel(normalList, setNormalList, item.id, 'whatsapp')} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">SMS</span>
                    <Toggle checked={item.sms} onChange={() => toggleChannel(normalList, setNormalList, item.id, 'sms')} disabled />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PanelLayout>
  );
}